(function () {
  const SECTION_SELECTOR = '[data-multicolumn-video-section]';
  const VIDEO_SELECTOR = 'video[data-multicolumn-video-key]';

  function parseBool(val) {
    return val === true || val === 'true' || val === 1 || val === '1';
  }

  async function ensureVideoSource(video) {
    if (video.__multicolumnSourceReady) return;

    const mp4Src = video.dataset.mp4Src;

    if (mp4Src) {
      try {
        video.src = mp4Src;
        video.load();
      } catch (e) {}
    }

    video.__multicolumnSourceReady = true;
  }

  function pauseVideo(video) {
    if (!video) return;
    try {
      video.pause();
    } catch (e) {}

    // Reset so when it becomes active again it starts from the beginning.
    try {
      video.currentTime = 0;
    } catch (e) {}
  }

  function updateMuteButton(button, video) {
    const isMuted = !!video.muted;

    const UNMUTE_ICON = `
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11 5L7 9H3V15H7L11 19V5Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
        <path d="M14.5 9.5L20 15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        <path d="M20 9.5L14.5 15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
      </svg>
    `.trim();

    const MUTE_ICON = `
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11 5L7 9H3V15H7L11 19V5Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
        <path d="M15 9.5C15.8 10.2 16.3 11 16.3 12C16.3 13 15.8 13.8 15 14.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        <path d="M17.4 7.4C18.8 8.6 19.7 10.1 19.7 12C19.7 13.9 18.8 15.4 17.4 16.6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
      </svg>
    `.trim();

    // When video is muted, button represents "Unmute" (and shows the unmute icon).
    button.innerHTML = isMuted ? UNMUTE_ICON : MUTE_ICON;
    button.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
    button.setAttribute('aria-pressed', String(!isMuted));
  }

  function bindMuteButtons(section, videoByKey) {
    const buttons = Array.from(section.querySelectorAll('[data-multicolumn-video-toggle-muted]'));
    const boundPairs = buttons
      .map((btn) => {
        const key = btn.dataset.videoKey;
        const video = key ? videoByKey.get(key) : null;
        return video ? { btn, video } : null;
      })
      .filter(Boolean);

    const syncAllMuteExcept = (videoToKeepUnmuted) => {
      boundPairs.forEach(({ btn, video }) => {
        // If keeping unmuted, only that one is unmuted.
        if (videoToKeepUnmuted && video === videoToKeepUnmuted) {
          video.muted = false;
        } else {
          video.muted = true;
        }
        updateMuteButton(btn, video);
      });
    };

    boundPairs.forEach(({ btn, video }) => {
      if (!btn.__multicolumnBound) {
        btn.__multicolumnBound = true;
        btn.addEventListener('click', () => {
          const willBeMuted = !video.muted;

          // Unmute one -> mute all others.
          if (!willBeMuted) {
            syncAllMuteExcept(video);
          } else {
            // Mute -> mute everything.
            syncAllMuteExcept(null);
          }
        });
      }

      updateMuteButton(btn, video);
    });
  }

  function getActiveVideoForTrack(track, videoEls) {
    const trackRect = track.getBoundingClientRect();
    const trackCenterX = trackRect.left + trackRect.width / 2;

    let bestVideo = null;
    let bestDist = Infinity;

    const seen = new Set();
    const cards = [];
    videoEls.forEach((video) => {
      const card = video.closest('.multicolumn-card');
      if (!card || seen.has(card)) return;
      seen.add(card);
      cards.push(card);
    });

    cards.forEach((card) => {
      const rect = card.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const dist = Math.abs(centerX - trackCenterX);
      if (dist < bestDist) {
        bestDist = dist;
        bestVideo = card.querySelector(VIDEO_SELECTOR);
      }
    });

    return bestVideo;
  }

  function initSection(section) {
    if (!section || section.dataset.multicolumnVideoInitialized === 'true') return;

    const videos = Array.from(section.querySelectorAll(VIDEO_SELECTOR));
    if (!videos.length) return;

    section.dataset.multicolumnVideoInitialized = 'true';

    const videoByKey = new Map(
      videos.map((v) => [v.dataset.multicolumnVideoKey, v])
    );

    // Apply default mute/loop based on block settings.
    videos.forEach((video) => {
      const mutedDefault = parseBool(video.dataset.multicolumnVideoMutedDefault);
      const loop = parseBool(video.dataset.multicolumnVideoLoop);
      const shouldAutoplay = parseBool(video.dataset.multicolumnVideoAutoplay);

      if (loop) video.loop = true;
      if (mutedDefault) video.muted = true;

      // Keep metadata preload; do not force autoplay from HTML attributes.
      video.dataset.multicolumnVideoShouldAutoplay = shouldAutoplay ? 'true' : 'false';
    });

    bindMuteButtons(section, videoByKey);

    const track = section.querySelector('.section__content.swipe-mobile');
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 767.98px)').matches;

    // Token used to ignore late async play attempts.
    const state = {
      activeKey: null,
      token: 0,
      rafQueued: false
    };

    async function playVideo(video, token) {
      if (!video) return;
      const key = video.dataset.multicolumnVideoKey;
      if (token !== state.token || key !== state.activeKey) return;

      const shouldAutoplay = parseBool(video.dataset.multicolumnVideoShouldAutoplay);
      if (!shouldAutoplay) {
        pauseVideo(video);
        return;
      }

      await ensureVideoSource(video);
      if (token !== state.token || key !== state.activeKey) return;

      // Best-effort play. Some browsers need a short wait until canplay.
      try {
        const p = video.play && video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch (e) {}

      // Retry once when it becomes playable.
      video.addEventListener(
        'canplay',
        () => {
          if (token !== state.token || key !== state.activeKey) return;
          try {
            const p2 = video.play && video.play();
            if (p2 && typeof p2.catch === 'function') p2.catch(() => {});
          } catch (e) {}
        },
        { once: true }
      );
    }

    function pauseAllExcept(activeVideo) {
      videos.forEach((v) => {
        if (!activeVideo || v !== activeVideo) pauseVideo(v);
      });
    }

    function setActive(nextVideo) {
      state.token += 1;
      const token = state.token;

      const nextKey = nextVideo ? nextVideo.dataset.multicolumnVideoKey : null;
      state.activeKey = nextKey;

      pauseAllExcept(nextVideo);
      playVideo(nextVideo, token);
    }

    function computeAndSetActive() {
      if (!track || !isMobile) return;
      const next = getActiveVideoForTrack(track, videos);
      if (!next) return;
      const nextKey = next.dataset.multicolumnVideoKey;
      if (nextKey && nextKey !== state.activeKey) setActive(next);
    }

    async function playOnLoadForNonMobile() {
      // For non-mobile (no exclusivity), respect per-block autoplay.
      videos.forEach(async (video) => {
        const shouldAutoplay = parseBool(video.dataset.multicolumnVideoShouldAutoplay);
        if (!shouldAutoplay) return;
        if (state.activeKey === null) state.activeKey = video.dataset.multicolumnVideoKey;
        await ensureVideoSource(video);
        try {
          const p = video.play && video.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        } catch (e) {}
      });
    }

    // Mobile exclusivity: only active card plays.
    if (track && isMobile) {
      state.activeKey = null;
      computeAndSetActive();
      track.addEventListener(
        'scroll',
        () => {
          if (state.rafQueued) return;
          state.rafQueued = true;
          requestAnimationFrame(() => {
            state.rafQueued = false;
            computeAndSetActive();
          });
        },
        { passive: true }
      );
      return;
    }

    // Otherwise: play videos that have autoplay enabled.
    playOnLoadForNonMobile();
  }

  function initAll() {
    const sections = Array.from(document.querySelectorAll(SECTION_SELECTOR));
    sections.forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:select', initAll);
})();

